<template>
  <div
    class="kanban-card relative flex flex-col p-3.5 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700/70 shadow-sm hover:shadow-md hover:border-primary-500/50 transition-all cursor-pointer select-none group"
    @click="$emit('click', $event)"
  >
    <!-- Topo: Avatar + Nome + Telefone + Datas (Criação • Última Atividade) -->
    <div class="flex items-start gap-2.5 mb-2">
      <Avatar
        :name="contact.name || 'Cliente'"
        :src="contact.thumbnail"
        :size="32"
        class="flex-shrink-0 mt-0.5"
        hide-offline-status
      />
      <div class="flex-1 min-w-0">
        <h4 class="text-xs font-bold text-slate-800 dark:text-slate-100 truncate leading-snug">
          {{ contact.name || 'Cliente #' + (chat.display_id || chat.id) }}
        </h4>
        <span v-if="contact.phone_number" class="text-[11px] font-medium text-slate-500 dark:text-slate-400 truncate block mt-0.5">
          {{ contact.phone_number }}
        </span>
      </div>
      <div class="flex-shrink-0 text-right">
        <span class="text-[10px] font-semibold text-slate-400 dark:text-slate-500">
          <TimeAgo
            :last-activity-timestamp="chat.last_activity_at || chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :conversation-id="chat.id"
          />
        </span>
      </div>
    </div>

    <!-- Meio: Resumo da Conversa (Áudio, Imagem, Doc, Vídeo ou Texto) -->
    <div class="text-xs text-slate-600 dark:text-slate-300 line-clamp-2 my-2 flex items-center gap-1.5 min-h-[20px] font-normal leading-relaxed">
      <span v-if="chat.preview" class="truncate flex-1">
        {{ chat.preview }}
      </span>
      <MessagePreview
        v-else-if="lastMessageInChat"
        :message="lastMessageInChat"
        class="truncate flex-1 text-xs"
      />
      <span v-else class="text-xs text-slate-400 italic">
        Nenhuma mensagem recente
      </span>
    </div>

    <!-- Rodapé: Caixa de Entrada + Conversa #ID à Esquerda e Agente à Direita (Tudo na mesma linha) -->
    <div class="flex items-center justify-between pt-2 mt-1 border-t border-slate-100 dark:border-slate-700/60 text-[11px] gap-1.5">
      <!-- Esquerda: Caixa de Entrada + Conversa #ID + Não lidas + Prioridade -->
      <div class="flex items-center gap-1.5 min-w-0 overflow-hidden">
        <InboxName
          v-if="resolvedInbox && resolvedInbox.name"
          :inbox="resolvedInbox"
          class="!text-[9.5px] !px-2 !py-0.2 shrink-0 max-w-[110px]"
        />
        <span class="text-[10.5px] font-bold text-slate-500 dark:text-slate-400 shrink-0">
          #{{ chat.display_id || chat.id }}
        </span>
        <span v-if="unreadCount > 0" class="px-1.5 py-0.2 rounded-full text-[9px] font-black bg-primary-500 text-white shadow-2xs shrink-0">
          {{ unreadCount }}
        </span>
        <CardPriorityIcon v-if="chat.priority" :priority="chat.priority" class="!size-3 shrink-0" />
      </div>

      <!-- Direita: Agente Responsável -->
      <div class="flex items-center gap-1 shrink-0 max-w-[125px] truncate">
        <div
          v-if="resolvedAssignee.name"
          class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9.5px] font-bold bg-primary-500/10 text-primary-600 dark:text-primary-400 border border-primary-500/20 truncate"
          title="Agente Responsável"
        >
          <Avatar
            v-if="resolvedAssignee.avatar_url"
            :src="resolvedAssignee.avatar_url"
            :size="13"
            class="flex-shrink-0"
          />
          <i v-else class="i-lucide-user-check text-xs flex-shrink-0 text-primary-500"></i>
          <span class="truncate">{{ resolvedAssignee.name }}</span>
        </div>
        <div
          v-else
          class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9.5px] font-medium bg-slate-100 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500 border border-slate-200/60 dark:border-slate-600/40 truncate"
        >
          <i class="i-lucide-user text-xs flex-shrink-0 text-slate-400"></i>
          <span class="truncate">Sem agente</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from 'dashboard/components/widgets/conversation/MessagePreview.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import InboxName from 'dashboard/components-next/Conversation/InboxName.vue';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, default: () => null },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
});

defineEmits(['click', 'contextmenu']);

const contact = computed(() => {
  if (props.currentContact && props.currentContact.name) return props.currentContact;
  if (props.chat && props.chat.meta && props.chat.meta.sender) return props.chat.meta.sender;
  return { name: `Cliente #${props.chat?.display_id || props.chat?.id || ''}` };
});

const resolvedAssignee = computed(() => {
  if (props.assignee && props.assignee.name) return props.assignee;
  if (props.chat && props.chat.meta && props.chat.meta.assignee) return props.chat.meta.assignee;
  return {};
});

const resolvedInbox = computed(() => {
  if (props.inbox && props.inbox.name) return props.inbox;
  if (props.chat && props.chat.inbox && props.chat.inbox.name) return props.chat.inbox;
  return null;
});

const unreadCount = computed(() => props.chat?.unread_count || 0);

const lastMessageInChat = computed(() => {
  try {
    return getLastMessage(props.chat);
  } catch (e) {
    return null;
  }
});
</script>
